import { useContext, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import NewSectionIcon from '../../Icons/NewSectionIcon';
import MainButton from '../MainButton';
import { useWindowSize } from '../../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../utils/constants';
import classes from './TimeandExpenseActions.module.scss';

import { EditContext, EditExpenseContext } from '../../../../context/editContexts';
import { PageContext } from '../../../../context/context';
import { generalEnums, submissionEnum } from '../../../../utils/enums';

import { buttonVariants } from '../types';
import colors from '../../../../scss/colors';

export default function TimeandExpenseActions({
  save,
  submit,
  reject,
  close,
  isRejectActive = true,
  approve,
  withdraw,
  submissionType,
  saving,
  rejecting,
  isApprover,
  status,
  sectionStatus,
  placementStatus,
  placementHandler,
  customTypes = [],
  sectionHandler = () => {},
  isSplitApprovalFlow = false
}) {
  const size = useWindowSize();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useId();
  const pageCtx = useContext(PageContext);
  const ctx = pageCtx.pageType === generalEnums.TIMESHEET ? useContext(EditContext) : useContext(EditExpenseContext);
  const [hoveredButton, setHoveredButton] = useState(false);
  const generateButtonLabel = (type) => {
    let resultLabel = '';
    switch (type) {
      case 'save':
        resultLabel += t('common.general.save');
        break;
      case 'submit':
        resultLabel += t('common.general.submit');
        break;
      case 'delete':
        resultLabel += t('common.general.delete');
        break;
      case 'close':
        resultLabel += t('common.general.close');
        break;
      default:
        resultLabel = '';
        break;
    }
    if (size.width < responsiveSizeTimesheet) return resultLabel;
    if (pageCtx.pageType !== generalEnums.EXPENSE) {
      resultLabel +=
        submissionType === submissionEnum.WEEKLY ? ` ${t('common.general.week')}` : ` ${t('common.general.month')}`;
    }
    return resultLabel;
  };
  const isSplitActionable =
    isSplitApprovalFlow &&
    status === timesheetsStatuses(t).rejected.apiStatus &&
    [timesheetsStatuses(t).pending.apiStatus, timesheetsStatuses(t).inreview.apiStatus].includes(sectionStatus);

  const isSectionApproved = isSplitApprovalFlow && [timesheetsStatuses(t).approved.apiStatus].includes(sectionStatus);

  if (
    isApprover &&
    status === timesheetsStatuses(t, isApprover).rejected.apiStatus &&
    !isSplitActionable &&
    !isSectionApproved
  ) {
    return false;
  }

  if ((isApprover && status === timesheetsStatuses(t, isApprover).approved.apiStatus) || isSectionApproved) {
    return (
      <div className={classes.container}>
        <MainButton
          key={id}
          size="small"
          variant={buttonVariants.PRIMARY}
          rounded
          outlined
          action={withdraw}
          forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
        >
          {t(size.width > responsiveSizeTimesheet ? 'common.general.withdrawapproval' : 'common.general.withdraw')}
        </MainButton>
        {size.width < responsiveSizeTimesheet && (
          <MainButton
            size="small"
            variant={buttonVariants.PRIMARY}
            rounded
            action={() => {
              navigate(pageCtx.pageType === generalEnums.TIMESHEET ? '/dashboard/timesheets' : '/dashboard/expenses');
            }}
            forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
          >
            {t('common.general.close')}
          </MainButton>
        )}
      </div>
    );
  }

  if (isApprover && (status === timesheetsStatuses(t, isApprover).submitted.apiStatus || isSplitActionable)) {
    return (
      <div className={classes.container}>
        <MainButton
          key={new Date()}
          size="small"
          variant={isRejectActive ? buttonVariants.PRIMARY : buttonVariants.DISABLED}
          outlined
          rounded
          action={reject}
          forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
        >
          {rejecting ? <CircularProgress size="20px" /> : t('common.general.reject')}
        </MainButton>
        <MainButton
          size="small"
          variant={buttonVariants.PRIMARY}
          rounded
          action={approve}
          forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
        >
          {t('common.general.approve')}
        </MainButton>
      </div>
    );
  }
  if (!isApprover && customTypes.length) {
    return (
      <div className={classes.container}>
        {customTypes.map((type) => (
          <MainButton
            key={type.label}
            size="small"
            variant={ctx.hasChanges ? buttonVariants.PRIMARY : buttonVariants.DISABLED}
            outlined={type.variant === buttonVariants.SECONDARY}
            rounded
            action={type.handler}
            disabled={type.disableHandler}
            forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
          >
            {!type.saving ? (
              generateButtonLabel(type.label)
            ) : (
              <CircularProgress size="20px" sx={{ color: '#003b58' }} />
            )}
          </MainButton>
        ))}
      </div>
    );
  }
  const saveOrCloseButtonHandler = () => {
    let buttonContent;
    if (size.width > responsiveSizeTimesheet) {
      if (!saving) {
        buttonContent = generateButtonLabel('save');
      } else {
        buttonContent = <CircularProgress size="20px" sx={{ color: colors.NAVY_BLUE }} />;
      }
    } else if (!saving) {
      buttonContent = ctx.hasChanges ? generateButtonLabel('save') : generateButtonLabel('close');
    } else {
      buttonContent = <CircularProgress size="20px" sx={{ color: colors.NAVY_BLUE }} />;
    }
    return buttonContent;
  };
  return ![timesheetsStatuses(t).approved.label, timesheetsStatuses(t).processed.label].includes(status) ? (
    <div className={classes.container}>
      {ctx?.isEdit ? (
        <>
          {isSplitApprovalFlow && size.width > responsiveSizeTimesheet && (
            <MainButton
              size="small"
              variant={buttonVariants.SECONDARY}
              outlined
              rounded
              action={sectionHandler}
              onHoverChange={setHoveredButton}
              forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
            >
              <div className="flex items-center gap-1 justify-center">
                <NewSectionIcon fill={hoveredButton ? colors.WHITE : colors.LIGHT_BLUE} />
                <span>{t('common.general.newApprovalSection')}</span>
              </div>
            </MainButton>
          )}
          <MainButton
            disabled={saving}
            size="small"
            variant={
              !ctx.hasChanges && size.width > responsiveSizeTimesheet ? buttonVariants.DISABLED : buttonVariants.PRIMARY
            }
            outlined
            rounded
            action={ctx.hasChanges ? save : close}
            forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
          >
            {saveOrCloseButtonHandler()}
          </MainButton>
          {status === timesheetsStatuses(t).draft.label ? (
            <MainButton
              size="small"
              variant={placementStatus ? buttonVariants.DISABLED : buttonVariants.PRIMARY}
              outlined={false}
              rounded
              action={placementStatus ? placementHandler : submit}
              forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
            >
              {generateButtonLabel('submit')}
            </MainButton>
          ) : (
            (status === timesheetsStatuses(t).submitted.label || status === timesheetsStatuses(t).rejected.label) && (
              <MainButton
                size="small"
                variant={ctx.hasChanges ? buttonVariants.PRIMARY : buttonVariants.DISABLED}
                disabled={placementStatus ? '' : !ctx.hasChanges}
                outlined={false}
                rounded
                action={placementStatus ? placementHandler : submit}
                forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
              >
                {t('common.general.resubmit')}
              </MainButton>
            )
          )}
        </>
      ) : (
        <MainButton
          size="small"
          variant={placementStatus ? buttonVariants.DISABLED : buttonVariants.PRIMARY}
          outlined
          rounded
          action={placementStatus ? placementHandler : () => ctx.setIsEdit(true)}
          forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
        >
          {t('common.general.edit')}
        </MainButton>
      )}
    </div>
  ) : (
    <MainButton
      size="small"
      variant={buttonVariants.PRIMARY}
      rounded
      action={() =>
        navigate(pageCtx.pageType === generalEnums.TIMESHEET ? '/dashboard/timesheets' : '/dashboard/expenses')
      }
      forceWidth={size.width > responsiveSizeTimesheet ? 'fit-content' : '100%'}
    >
      {t('common.general.close')}
    </MainButton>
  );
}
